/**
 * Premium Content Data
 *
 * Interview prep questions, salary negotiation tips, and employer data
 * for the PathFinder Complete tier.
 */

export interface InterviewQuestion {
  question: string;
  tip: string;
  sample_answer?: string;
}

export interface SalaryNegotiationTip {
  title: string;
  description: string;
  example?: string;
}

export interface Employer {
  name: string;
  type: 'Fortune 500' | 'Startup' | 'Mid-size' | 'Government' | 'Healthcare System' | 'Contractor';
  industry: string;
  hiring_url?: string;
  notes?: string;
}

export interface PremiumCareerContent {
  career_id: string;
  interview_questions: InterviewQuestion[];
  salary_tips: SalaryNegotiationTip[];
  employers: Employer[];
  cover_letter_tips: string[];
  linkedin_keywords: string[];
}

// Generic content for careers without specific data
const GENERIC_INTERVIEW_QUESTIONS: InterviewQuestion[] = [
  {
    question: "Tell me about yourself.",
    tip: "Focus on your professional journey and what led you to this career. Keep it to 2-3 minutes.",
    sample_answer: "I've been working in [current field] for [X years], where I developed strong [relevant skill]. I'm excited about transitioning to [target career] because..."
  },
  {
    question: "Why do you want to work here?",
    tip: "Research the company beforehand. Mention specific projects, values, or products that attract you.",
  },
  {
    question: "What are your strengths?",
    tip: "Choose 2-3 strengths directly relevant to the job. Provide specific examples.",
  },
  {
    question: "Where do you see yourself in 5 years?",
    tip: "Show ambition while being realistic. Align your goals with the company's growth opportunities.",
  },
  {
    question: "Tell me about a challenge you overcame.",
    tip: "Use the STAR method: Situation, Task, Action, Result. Quantify results when possible.",
  },
];

const GENERIC_SALARY_TIPS: SalaryNegotiationTip[] = [
  {
    title: "Do Your Research First",
    description: "Use Glassdoor, PayScale, and Bureau of Labor Statistics to know the market rate for your area.",
    example: "Based on my research, the market rate for this role in [city] is $X-$Y."
  },
  {
    title: "Let Them Name a Number First",
    description: "If asked for salary expectations early, try to defer until you understand the full compensation package.",
  },
  {
    title: "Negotiate Total Compensation",
    description: "Consider benefits, PTO, remote work, signing bonus, and professional development - not just base salary.",
  },
  {
    title: "Use Silence Strategically",
    description: "After they make an offer, pause before responding. This shows you're seriously considering it.",
  },
  {
    title: "Get It In Writing",
    description: "Once you agree, request a written offer letter with all discussed terms before giving notice at your current job.",
  },
];

const GENERIC_COVER_LETTER_TIPS = [
  "Address the hiring manager by name if possible - never use 'To Whom It May Concern'",
  "Open with why you're excited about THIS specific company, not just the role",
  "Highlight 2-3 relevant achievements with quantified results",
  "Show you've researched the company's challenges and how you can help solve them",
  "Keep it to one page maximum - 3-4 paragraphs",
];

const GENERIC_LINKEDIN_KEYWORDS = [
  "Open to work",
  "Career changer",
  "Quick learner",
  "Results-driven",
];

// Career-specific premium content
const PREMIUM_CONTENT_BY_CAREER: Record<string, Partial<PremiumCareerContent>> = {
  // Customer Success / Customer Service Roles
  customer_success_specialist: {
    interview_questions: [
      {
        question: "How do you handle an upset customer?",
        tip: "Describe your de-escalation process. Show empathy first, then problem-solving.",
        sample_answer: "First, I listen actively and acknowledge their frustration. Then I apologize for the inconvenience, clarify the issue, and present a clear solution with a timeline."
      },
      {
        question: "How do you prioritize when multiple customers need help?",
        tip: "Show your triage method - urgency, impact, and SLA considerations.",
      },
      {
        question: "Tell me about a time you turned an unhappy customer into a loyal one.",
        tip: "Use a specific example with measurable outcomes (retention, upsell, NPS score).",
      },
      {
        question: "How do you stay organized with a high volume of tickets?",
        tip: "Mention specific tools (Zendesk, Intercom, HubSpot) and your workflow.",
      },
      {
        question: "What does excellent customer service mean to you?",
        tip: "Define it in terms of outcomes: resolution time, customer satisfaction, proactive support.",
      },
    ],
    salary_tips: [
      {
        title: "Highlight Revenue Impact",
        description: "Customer success directly impacts retention. Quantify any retention improvements or upsells you've driven.",
        example: "I helped improve customer retention by 15% through proactive outreach."
      },
      {
        title: "Mention Tool Proficiency",
        description: "Experience with Salesforce, Zendesk, Intercom, or HubSpot can add $5-10K to offers.",
      },
      {
        title: "Negotiate for Remote",
        description: "Many CS roles are fully remote. This can offset a slightly lower salary with commute savings.",
      },
    ],
    employers: [
      { name: "Salesforce", type: "Fortune 500", industry: "Technology", hiring_url: "https://salesforce.com/careers" },
      { name: "HubSpot", type: "Mid-size", industry: "Technology", hiring_url: "https://hubspot.com/careers" },
      { name: "Zendesk", type: "Mid-size", industry: "Technology", hiring_url: "https://zendesk.com/jobs" },
      { name: "Intercom", type: "Mid-size", industry: "Technology", hiring_url: "https://intercom.com/careers" },
      { name: "Gainsight", type: "Mid-size", industry: "Technology", notes: "Leader in CS software" },
    ],
    linkedin_keywords: [
      "Customer Success",
      "Account Management",
      "Client Retention",
      "SaaS",
      "CRM",
      "Customer Experience",
    ],
  },

  // Software Development / Tech
  web_developer: {
    interview_questions: [
      {
        question: "Walk me through a project you've built.",
        tip: "Prepare 2-3 projects to discuss in depth. Focus on problem, solution, and your specific contributions.",
      },
      {
        question: "How do you stay current with web technologies?",
        tip: "Mention specific resources: podcasts, newsletters, GitHub following, side projects.",
      },
      {
        question: "Explain the difference between REST and GraphQL.",
        tip: "Know the trade-offs of each. Mention real-world use cases.",
      },
      {
        question: "How do you debug a performance issue?",
        tip: "Walk through your systematic approach: profiling, network waterfall, code review.",
      },
      {
        question: "Describe your experience with Git and collaboration.",
        tip: "Discuss branching strategies, code reviews, and CI/CD experience.",
      },
    ],
    salary_tips: [
      {
        title: "Portfolio is Leverage",
        description: "A strong GitHub profile or portfolio can add $10-20K to entry-level offers.",
      },
      {
        title: "Remote Commands Premium",
        description: "Fully remote roles often pay based on SF/NYC rates regardless of your location.",
      },
      {
        title: "Negotiate Equity in Startups",
        description: "Early-stage startups may offer lower base but significant equity upside.",
      },
    ],
    employers: [
      { name: "Google", type: "Fortune 500", industry: "Technology", hiring_url: "https://careers.google.com" },
      { name: "Meta", type: "Fortune 500", industry: "Technology", hiring_url: "https://metacareers.com" },
      { name: "Shopify", type: "Mid-size", industry: "E-commerce", hiring_url: "https://shopify.com/careers" },
      { name: "Stripe", type: "Mid-size", industry: "Fintech", hiring_url: "https://stripe.com/jobs" },
      { name: "Vercel", type: "Startup", industry: "Developer Tools", hiring_url: "https://vercel.com/careers" },
    ],
    linkedin_keywords: [
      "Full Stack Developer",
      "React",
      "JavaScript",
      "TypeScript",
      "Node.js",
      "Web Development",
    ],
  },

  // Healthcare
  medical_assistant: {
    interview_questions: [
      {
        question: "How do you handle patient confidentiality?",
        tip: "Show HIPAA knowledge. Give examples of protecting patient information.",
      },
      {
        question: "Tell me about a time you handled a medical emergency.",
        tip: "Describe staying calm, following protocol, and communicating with the healthcare team.",
      },
      {
        question: "How do you manage your time when the office is busy?",
        tip: "Show your triage abilities and prioritization skills.",
      },
      {
        question: "What EHR systems have you used?",
        tip: "Mention specific systems like Epic, Cerner, or Athena. Show willingness to learn new systems.",
      },
      {
        question: "How do you make patients feel comfortable?",
        tip: "Empathy is key. Describe your bedside manner and communication approach.",
      },
    ],
    salary_tips: [
      {
        title: "Certifications Add Value",
        description: "CMA or RMA certification can add $2-5K to starting salary.",
      },
      {
        title: "Specialty Clinics Pay More",
        description: "Dermatology, cardiology, and surgery clinics often pay 10-15% higher than general practice.",
      },
      {
        title: "Consider Sign-On Bonuses",
        description: "Many healthcare employers offer $1-3K signing bonuses due to worker shortages.",
      },
    ],
    employers: [
      { name: "Kaiser Permanente", type: "Healthcare System", industry: "Healthcare", hiring_url: "https://jobs.kaiserpermanente.org" },
      { name: "HCA Healthcare", type: "Healthcare System", industry: "Healthcare", hiring_url: "https://hcahealthcare.com/careers" },
      { name: "CVS Health", type: "Fortune 500", industry: "Healthcare Retail", hiring_url: "https://jobs.cvshealth.com" },
      { name: "One Medical", type: "Mid-size", industry: "Primary Care", hiring_url: "https://onemedical.com/careers" },
      { name: "Concentra", type: "Mid-size", industry: "Occupational Health", notes: "Focuses on workplace injuries" },
    ],
    linkedin_keywords: [
      "Certified Medical Assistant",
      "CMA",
      "Healthcare",
      "Patient Care",
      "EHR",
      "Phlebotomy",
    ],
  },

  // Project Management
  project_manager: {
    interview_questions: [
      {
        question: "Describe your project management methodology.",
        tip: "Know Agile, Scrum, Waterfall. Explain when you'd use each and why.",
      },
      {
        question: "How do you handle scope creep?",
        tip: "Show your change management process and stakeholder communication.",
      },
      {
        question: "Tell me about a project that went off track. How did you recover?",
        tip: "Be honest about challenges but focus on your problem-solving and learnings.",
      },
      {
        question: "How do you manage remote team members?",
        tip: "Discuss communication tools, async work, and building team culture virtually.",
      },
      {
        question: "What PM tools are you proficient in?",
        tip: "Mention Jira, Asana, Monday.com, MS Project, or Notion. Show adaptability to new tools.",
      },
    ],
    salary_tips: [
      {
        title: "PMP Certification Premium",
        description: "PMP certified project managers earn 20-25% more than non-certified peers.",
      },
      {
        title: "Industry Matters",
        description: "Tech and finance PM roles pay 30-40% more than other industries.",
      },
      {
        title: "Quantify Your Impact",
        description: "Come with numbers: project budgets managed, team sizes led, efficiency improvements.",
        example: "I managed a $2M project and delivered it 10% under budget."
      },
    ],
    employers: [
      { name: "Amazon", type: "Fortune 500", industry: "Technology", hiring_url: "https://amazon.jobs", notes: "Constant hiring for TPMs" },
      { name: "McKinsey", type: "Mid-size", industry: "Consulting", hiring_url: "https://mckinsey.com/careers" },
      { name: "Deloitte", type: "Fortune 500", industry: "Consulting", hiring_url: "https://deloitte.com/careers" },
      { name: "Boeing", type: "Fortune 500", industry: "Aerospace", hiring_url: "https://boeing.com/careers" },
      { name: "Lockheed Martin", type: "Fortune 500", industry: "Defense", notes: "Great for veterans" },
    ],
    linkedin_keywords: [
      "PMP",
      "Scrum Master",
      "Agile",
      "Project Manager",
      "Program Manager",
      "JIRA",
    ],
  },

  // Sales
  sales_representative: {
    interview_questions: [
      {
        question: "Sell me this pen.",
        tip: "Don't just describe features. Ask questions to understand needs, then position as solution.",
      },
      {
        question: "How do you handle rejection?",
        tip: "Show resilience and learning mindset. Sales is a numbers game.",
      },
      {
        question: "What's your sales process?",
        tip: "Describe prospecting, qualifying, presenting, handling objections, and closing.",
      },
      {
        question: "Tell me about your biggest sale.",
        tip: "Walk through the full process, emphasizing relationship building and understanding client needs.",
      },
      {
        question: "How do you stay motivated?",
        tip: "Connect to personal goals, competition, and the satisfaction of helping customers.",
      },
    ],
    salary_tips: [
      {
        title: "Know the OTE",
        description: "Ask about On-Target Earnings. A lower base with high commission potential can be lucrative.",
        example: "$50K base + $50K commission OTE = $100K total"
      },
      {
        title: "Negotiate the Ramp Period",
        description: "Ask for a guaranteed minimum during your first 3-6 months while building pipeline.",
      },
      {
        title: "Understand the Commission Structure",
        description: "Is it capped? Accelerators after quota? Clawbacks if customers cancel?",
      },
    ],
    employers: [
      { name: "Salesforce", type: "Fortune 500", industry: "Technology", hiring_url: "https://salesforce.com/careers" },
      { name: "Oracle", type: "Fortune 500", industry: "Technology", hiring_url: "https://oracle.com/careers" },
      { name: "Enterprise Rent-A-Car", type: "Fortune 500", industry: "Automotive", notes: "Great sales training program" },
      { name: "ADP", type: "Fortune 500", industry: "HR Technology", hiring_url: "https://jobs.adp.com" },
      { name: "Gartner", type: "Fortune 500", industry: "Research", notes: "High-earning sales roles" },
    ],
    linkedin_keywords: [
      "Account Executive",
      "Business Development",
      "Sales Representative",
      "B2B Sales",
      "SaaS Sales",
      "Quota Attainment",
    ],
  },

  // Trades
  electrician: {
    interview_questions: [
      {
        question: "Describe your experience with the NEC (National Electrical Code).",
        tip: "Show current knowledge of code requirements and recent changes.",
      },
      {
        question: "How do you prioritize safety on the job?",
        tip: "Discuss specific safety protocols, PPE, and lockout/tagout procedures.",
      },
      {
        question: "Tell me about a complex wiring problem you solved.",
        tip: "Walk through your diagnostic process and solution.",
      },
      {
        question: "What's your experience with blueprints?",
        tip: "Describe reading schematics, making field adjustments, and coordinating with other trades.",
      },
      {
        question: "How do you stay current with electrical technology?",
        tip: "Mention continuing education, certifications, and new technologies like solar or EV charging.",
      },
    ],
    salary_tips: [
      {
        title: "Union vs Non-Union",
        description: "Union electricians often earn 20-30% more with better benefits, but may have slower advancement.",
      },
      {
        title: "Specializations Pay Premium",
        description: "Industrial, solar, and data center electricians command higher rates than residential.",
      },
      {
        title: "Get Licensed",
        description: "Journeyman license opens doors. Master Electrician license can add $10-20K to your earnings.",
      },
    ],
    employers: [
      { name: "IBEW (Union)", type: "Contractor", industry: "Construction", notes: "International Brotherhood of Electrical Workers" },
      { name: "Pike Electric", type: "Contractor", industry: "Utilities", hiring_url: "https://pikeelectric.com/careers" },
      { name: "Tesla", type: "Fortune 500", industry: "Energy", notes: "Solar and energy storage installations" },
      { name: "Siemens", type: "Fortune 500", industry: "Industrial", hiring_url: "https://siemens.com/careers" },
      { name: "Amazon (Data Centers)", type: "Fortune 500", industry: "Technology", notes: "Critical power systems" },
    ],
    linkedin_keywords: [
      "Electrician",
      "Journeyman",
      "Master Electrician",
      "NEC",
      "Industrial Electrical",
      "Solar Installation",
    ],
  },
};

/**
 * Get premium content for a specific career
 */
export function getPremiumContent(careerId: string): PremiumCareerContent {
  const specificContent = PREMIUM_CONTENT_BY_CAREER[careerId] || {};

  return {
    career_id: careerId,
    interview_questions: specificContent.interview_questions || GENERIC_INTERVIEW_QUESTIONS,
    salary_tips: specificContent.salary_tips || GENERIC_SALARY_TIPS,
    employers: specificContent.employers || [],
    cover_letter_tips: specificContent.cover_letter_tips || GENERIC_COVER_LETTER_TIPS,
    linkedin_keywords: specificContent.linkedin_keywords || GENERIC_LINKEDIN_KEYWORDS,
  };
}

/**
 * Check if career has specific premium content (vs generic)
 */
export function hasSpecificPremiumContent(careerId: string): boolean {
  return careerId in PREMIUM_CONTENT_BY_CAREER;
}
